import React, { useRef, useState } from 'react';
import PageHeader from './components/PageHeader';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import { usePackageLocationSelection } from './hooks/usePackageLocationSelection';

export default function StagePackageDialog({ onClose, onNext, organizationId }) {
  const { markets, facilities, marketId, facilityId, selectMarket, selectFacility } =
    usePackageLocationSelection(organizationId, true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const isSubmittingRef = useRef(false);
  const { close, isClosing } = useAnimatedClose(onClose);

  const validate = () => {
    const next: Record<string, any> = {};
    if (!marketId) next.market = 'Market is required';
    if (!facilityId) next.facility = 'Facility is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
      if (isSubmittingRef.current) return;
      if (!validate()) return;
      const selectedFacility = facilities.find(f => f.id == facilityId);
      if (!selectedFacility) {
        setErrors({ facility: 'Facility is required' });
        return;
      }
      const { name, metrcFacilityId, license } = selectedFacility;
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      try {
        await onNext({
            marketId,
            facilityId,
            metrcFacilityId,
            marketName: markets.find(m => marketId == m.id)?.name,
            facilityName: `${name} ${license}`
        });
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
  };

  const footer = (
      <DialogFooter>
          <button
            className="button"
            onClick={handleSave}
            disabled={!facilityId || !marketId || isSubmitting}
            aria-busy={isSubmitting}
            data-testid="stage-package-next"
          >
            {isSubmitting ? 'Loading…' : 'Next'}
          </button>
    </DialogFooter>
  );

  return (
    <div className={`stack page-slide-in${isClosing ? ' page-slide-out' : ''}`} data-testid="stage-package">
      <PageHeader
        title="Stage Packages"
        subtitle="Packages"
        actions={(
          <button className="button secondary" onClick={close} disabled={isSubmitting}>Back</button>
        )}
      />

      <div className="drawer-body">
        <div className="drawer-preview">
          <div className="card" style={{ padding: 16 }}>
            <div className="muted" style={{ fontSize: 12, fontWeight: 800, marginBottom: 8 }}>Preview</div>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Staging Setup</div>
            <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 6 }}>
               <div><span style={{ fontWeight: 700, color: 'var(--primary)' }}>Available Facilities:</span> {facilities.length}</div>
            </div>
            <div className="divider" style={{ margin: '12px 0' }} />
            <div className="muted" style={{ fontSize: 12 }}>Next you’ll choose tags to stage.</div>
          </div>
        </div>
        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} />

            <div className="form-grid">
              <div className="field full-span">
                <label className="label">Market <span className="required-dot" aria-label="required">*</span></label>
                <select
                  className={`select ${errors.market ? 'error' : ''}`}
                  value={marketId}
                  onChange={(e) => selectMarket(e.target.value)}
                  data-testid="stage-package-market"
                >
                  <option value="">Select market</option>
                  {[...markets].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                {errors.market && <div className="error-text">{errors.market}</div>}
              </div>

              <div className="field full-span">
                <label className="label">Facility <span className="required-dot" aria-label="required">*</span></label>
                <select
                  className={`select ${errors.facility ? 'error' : ''}`}
                  value={facilityId}
                  onChange={(e) => selectFacility(e.target.value)}
                  disabled={!marketId}
                  data-testid="stage-package-facility"
                >
                                  <option value="">Select facility</option>
                                  {[...facilities]
                    .sort((a, b) => `${a.name} — ${a.license}`.localeCompare(`${b.name} — ${b.license}`, undefined, { sensitivity: 'base' }))
                    .map((f) => (
                    <option key={f.id} value={f.id}>{f.name} — {f.license}</option>
                  ))}
                </select>
                {errors.facility && <div className="error-text">{errors.facility}</div>}
              </div>
            </div>

            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}
